import { ACTOR_POSTURE, type Aabb, type InteriorTemplate, type StoreThemeId, type Vec2, type WorldManifest } from "@townsy/contracts";
import { getCatalogItem } from "./catalog";
import { pointInsideAny } from "./collision";
import { fencedPlotIds, interiorDecorationColliders, yardDecorationColliders, type PlacementRef } from "./decorations";
import { itemDistrictId } from "./districtItems";
import { distance, forward, wrapAngle } from "./geometry";
import { WORLD_MANIFEST } from "./manifest";
import { CENTRAL_DISTRICT_ID } from "./plots";
import { homeCollider, interiorColliders } from "./queries";
import { buildColliderIndex, type ColliderSet } from "./spatialIndex";
import { STORE_ROOM_BOUNDS, storeInteriorColliders, storeTheme, type StoreFixtureKind } from "./storeThemes";
import { streetDecor } from "./streetDecor";

/**
 * Seats (founder request 2026-10-13, ADR-0018: "the ability for the avatar to sit in chairs and lay in the bed").
 *
 * A seat is one spot where ONE person can sit or lie: a sofa has three, a double bed two lie spots, a bench three. The
 * seats of a room are listed by a pure function of shared data, so the realtime server (which seats people) and the
 * browser (which offers "Sit" and draws the posture) list exactly the same seats with the same ids:
 *  - homes: `homeSeats(template, placements)` from the furniture placed on the home_room anchors;
 *  - the street: `streetSeats(manifest, yard)` from the plaza benches (manifest props) and the yard items with seats
 *    (patio sets, hot tubs, the pool lounger) of the owned homes;
 *  - stores: `storeSeats(theme)` from the theme's seating fixtures (cafe tables, sofas, waiting chairs, booths...).
 * Positions are where the HIPS go (the server puts the actor's x/z there), `y` is the height of the surface sat or
 * lain on, `facing` is the way a sitter faces (a lying person: from the head to the feet). Every seat has a
 * stand-up spot outside every collider of the room (tests prove it for every anchor and rotation); the server still
 * checks it at run time and uses the nearest free spot if someone stands there or something new was placed.
 *
 * Local spot coordinates follow the models (apps/web/src/scene/decor/items.ts for catalog items,
 * scene/interior/fixtureModels.ts for store fixtures, scene/town/props.ts for benches): +z = the item's front.
 */

export type SeatKind = "sit" | "lie";

export interface Seat {
  /** Stable id within the room (<= 64 characters): the same furniture on the same anchor keeps its ids. */
  id: string;
  kind: SeatKind;
  /** Where the hips go (room coordinates). */
  x: number;
  z: number;
  /** Yaw: the way a sitter faces; for a lying person, from the head to the feet. */
  facing: number;
  /** Height of the surface sat or lain on (metres above the floor / street ground plane). */
  y: number;
  /** Where the person stands up (free of every collider of the room as listed). */
  stand: Vec2;
  /** For the HUD: "on the sofa", "in the hot tub", "at the bar". */
  place: string;
  /**
   * Street yard seats only: the residential plot (home) the item stands on. While that home is closed (ADR-0019) only
   * its owner may sit or lie there; the plaza benches and indoor seats have none.
   */
  plotId?: string;
}

/** How close (metres, actor to seat) the "Sit" / "Lie down" prompt appears; the server allows SEAT_REACH_TOLERANCE more. */
export const SEAT_REACH_METRES = 1.6;
/** Lag tolerance the server adds to the reach (the prompt is computed from the predicted position). */
export const SEAT_REACH_TOLERANCE_METRES = 0.35;
/** Lying: the head rests this far behind the hips (the lie spots are placed from the pillow). */
export const LIE_HEAD_TO_HIPS = 0.7;
/** Lying: the feet end this far in front of the hips. */
export const LIE_HIPS_TO_FEET = 0.92;

/** Posture code (ActorState.posture) for a seat kind. */
export function postureOf(kind: SeatKind): 1 | 2 {
  return kind === "lie" ? ACTOR_POSTURE.lying : ACTOR_POSTURE.sitting;
}

// ---------------------------------------------------------------------------------------------
// Seat spots per model (local frame)
// ---------------------------------------------------------------------------------------------

interface LocalPoint {
  x: number;
  z: number;
}

interface SeatSpot extends LocalPoint {
  kind: SeatKind;
  /** Local yaw (0 = the item's front). */
  facing: number;
  y: number;
  /** Stand-up candidates in order of preference (the first free one is used). */
  stand: readonly LocalPoint[];
}

interface SeatSpec {
  place: string;
  spots: readonly SeatSpot[];
}

const sit = (x: number, z: number, y: number, facing: number, stand: LocalPoint[]): SeatSpot => ({ kind: "sit", x, z, y, facing, stand });
const lie = (x: number, z: number, y: number, facing: number, stand: LocalPoint[]): SeatSpot => ({ kind: "lie", x, z, y, facing, stand });
const P = (x: number, z: number): LocalPoint => ({ x, z });
const sgn = (v: number) => (v < 0 ? -1 : 1);

/** Chairs facing the centre of a round set (patio set, hot tub): spot i at angle PI/4 + i * PI/2. */
function ring(radius: number, y: number, standOut: number, place: string): SeatSpec {
  const spots: SeatSpot[] = [];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const x = Math.cos(a) * radius;
    const z = Math.sin(a) * radius;
    const sx = sgn(x);
    const sz = sgn(z);
    // Facing the centre; stand beside the set on either side, else out on the diagonal.
    spots.push(sit(x, z, y, Math.atan2(-x, -z), [P(sx * standOut, sz * 0.6), P(sx * 0.6, sz * standOut), P(sx * standOut * 0.95, sz * standOut * 0.95)]));
  }
  return { place, spots };
}

/**
 * Catalog furniture and yard items with seats (packages/world catalog.ts ids; models in scene/decor/items.ts):
 *  - sofa: three cushions at x -0.68 / 0 / 0.68 (top 0.64; the body sinks 6 cm), backrest at -z;
 *  - bed: double bed, headboard at -z, pillows at x +-0.42, z -0.78 (top 0.78), quilt top 0.68: two lie spots;
 *  - armchair: cushion top 0.56 at z 0.08, back at -0.32;
 *  - dining_table: four chairs at (+-0.4, +-0.6), seats 0.49, facing the table;
 *  - patio_set: four chairs on the diagonals 0.85 m out (seats 0.48) facing the table;
 *  - hot_tub: four places on the diagonals in the water (seat ledge 0.42 m), facing the middle;
 *  - pool_small: the lounger on the end (top 0.34) at x -1.0, z -3.0 .. -1.6.
 */
const ITEM_SEATS: Record<string, SeatSpec> = {
  sofa: { place: "on the sofa", spots: [-0.68, 0, 0.68].map((x) => sit(x, 0.05, 0.58, 0, [P(x, 1.0), P(x, 1.4), P(sgn(x || -1) * 1.55, 0.2), P(-sgn(x || -1) * 1.55, 0.2)])) },
  bed: {
    place: "on the bed",
    spots: [-0.42, 0.42].map((x) => lie(x, -0.78 + LIE_HEAD_TO_HIPS, 0.66, 0, [P(sgn(x) * 1.45, 0.2), P(sgn(x) * 1.45, 0.9), P(x, 1.65), P(-sgn(x) * 1.45, 0.2)])),
  },
  armchair: { place: "in the armchair", spots: [sit(0, 0.02, 0.52, 0, [P(0, 0.95), P(-0.95, 0.2), P(0.95, 0.2), P(0, 1.35)])] },
  dining_table: {
    place: "at the table",
    spots: [-0.4, 0.4].flatMap((x) => [-1, 1].map((sz) => sit(x, sz * 0.62, 0.47, sz > 0 ? Math.PI : 0, [P(x, sz * 1.2), P(sgn(x) * 1.4, sz * 0.6), P(x, sz * 1.6)]))),
  },
  patio_set: ring(0.88, 0.46, 1.65, "at the patio table"),
  hot_tub: ring(0.62, 0.42, 1.75, "in the hot tub"),
  pool_small: { place: "on the pool lounger", spots: [lie(-1.0, -2.9 + LIE_HEAD_TO_HIPS, 0.34, 0, [P(-2.45, -2.3), P(-1.0, -3.45), P(-2.45, -1.0), P(-2.45, -3.45)])] },
};

/**
 * Plaza bench (scene/town/props.ts benchParts, built from y = 0): 1.7 m seat with its top at 0.50 (0.34 m above the
 * plaza paving), backrest at -z; three places.
 */
const BENCH_SEATS: SeatSpec = { place: "on the bench", spots: [-0.55, 0, 0.55].map((x) => sit(x, 0, 0.49, 0, [P(x, 0.85), P(x, 1.25), P(x, 1.7)])) };

/**
 * Store fixtures with seats (packages/world STORE_FIXTURE_KINDS; models in scene/interior/fixtureModels.ts). Desk
 * chairs, the meeting pod and the gym bench are not seats.
 */
const FIXTURE_SEATS: Partial<Record<StoreFixtureKind, SeatSpec>> = {
  // Round table with a chair at z +-0.56 (seat 0.49) facing it.
  bistro_set: { place: "at the café table", spots: [-1, 1].map((sz) => sit(0, sz * 0.6, 0.47, sz > 0 ? Math.PI : 0, [P(0, sz * 1.35), P(1.25, sz * 0.56), P(-1.25, sz * 0.56)])) },
  // Two cushions at x +-0.45 (top 0.46), backrest front at z -0.23.
  lounge_sofa: { place: "on the sofa", spots: [-0.45, 0.45].map((x) => sit(x, 0.02, 0.43, 0, [P(x, 1.0), P(x, 1.4), P(sgn(x) * 1.55, 0.2)])) },
  // Armchair at x -0.12 (cushion top 0.46) with a side table on its right.
  reading_chair: { place: "in the reading chair", spots: [sit(-0.12, 0.0, 0.42, 0, [P(-0.12, 0.95), P(-0.95, 0.1), P(0.95, 0.3), P(-0.12, 1.35)])] },
  // Three chairs at x -0.79 / 0 / 0.79 (seats 0.47).
  waiting_chairs: { place: "in a waiting chair", spots: [-0.79, 0, 0.79].map((x) => sit(x, 0.05, 0.45, 0, [P(x, 0.9), P(x, 1.3)])) },
  // Three chairs on each long side (z +-0.57) and one at each end (x +-1.27), seats 0.49, facing the table.
  conference_table: {
    place: "at the meeting table",
    spots: [
      ...[-0.8, 0, 0.8].flatMap((x) => [-1, 1].map((sz) => sit(x, sz * 0.62, 0.47, sz > 0 ? Math.PI : 0, [P(x, sz * 1.35), P(x, sz * 1.75)]))),
      ...[-1, 1].map((sx) => sit(sx * 1.32, 0, 0.47, sx > 0 ? -Math.PI / 2 : Math.PI / 2, [P(sx * 2.1, 0), P(sx * 2.1, 0.6), P(sx * 2.1, -0.6)])),
    ],
  },
  // Two customer chairs at (+-0.45, 0.52) facing the agent's desk.
  travel_desk: { place: "at the travel desk", spots: [-0.45, 0.45].map((x) => sit(x, 0.57, 0.47, Math.PI, [P(x, 1.35), P(sgn(x) * 1.35, 0.5)])) },
  // Tufted bench (top 0.44) along -z with a round table in front: three places.
  velvet_booth: { place: "in the booth", spots: [-0.6, 0, 0.6].map((x) => sit(x, -0.3, 0.42, 0, [P(x, 1.25), P(x, 1.65)])) },
  // Four stools (seats 0.75) on the front side, facing the counter.
  bar_counter: { place: "at the bar", spots: [-1.08, -0.36, 0.36, 1.08].map((x) => sit(x, 0.4, 0.74, Math.PI, [P(x, 1.15), P(x, 1.55)])) },
  // Two velvet armchairs (cushion 0.5) at x +-0.71 facing each other across a low table.
  low_table_set: {
    place: "in the armchair",
    spots: [-1, 1].map((sx) => sit(sx * 0.8, 0, 0.47, sx > 0 ? -Math.PI / 2 : Math.PI / 2, [P(sx * 0.71, 1.15), P(sx * 0.71, -1.15), P(sx * 1.55, 0)])),
  },
  // Backless bench (top 0.45): two places on each long side, back to back.
  gallery_bench: {
    place: "on the bench",
    spots: [-0.45, 0.45].flatMap((x) => [-1, 1].map((sz) => sit(x, sz * 0.12, 0.43, sz > 0 ? 0 : Math.PI, [P(x, sz * 0.85), P(sgn(x) * 1.35, sz * 0.45), P(x, sz * 1.25)]))),
  },
  // A big beanbag (top 0.59; you sink to 0.38).
  beanbag: { place: "on the beanbag", spots: [sit(0, 0.0, 0.38, 0, [P(0, 0.95), P(0.95, 0), P(-0.95, 0), P(0, -0.95)])] },
};

/** Catalog items that have seats (homes and yards). */
export const SEAT_ITEM_IDS: readonly string[] = Object.keys(ITEM_SEATS);
/** Store fixture kinds that have seats. */
export const SEAT_FIXTURE_KINDS: readonly StoreFixtureKind[] = Object.keys(FIXTURE_SEATS) as StoreFixtureKind[];

/** Number of seats a catalog item or store fixture offers (0 when it has none). */
export function seatCount(kind: string): number {
  return (ITEM_SEATS[kind] ?? FIXTURE_SEATS[kind as StoreFixtureKind])?.spots.length ?? 0;
}

// ---------------------------------------------------------------------------------------------
// Placing specs in a room
// ---------------------------------------------------------------------------------------------

/** cos / sin of a yaw with quarter-turn noise removed (cos(PI/2) is 6e-17, not 0). */
function turn(yaw: number): { c: number; s: number } {
  const snap = (v: number) => (Math.abs(v) < 1e-9 ? 0 : Math.abs(Math.abs(v) - 1) < 1e-9 ? Math.sign(v) : v);
  return { c: snap(Math.cos(yaw)), s: snap(Math.sin(yaw)) };
}

/** Local -> room coordinates (Babylon RotationY, like LocalBuilder.toWorld): (ox + x cos + z sin, oz - x sin + z cos). */
function toRoom(o: Vec2, yaw: number, scale: number, p: LocalPoint): Vec2 {
  const { c, s } = turn(yaw);
  return { x: o.x + (p.x * c + p.z * s) * scale, z: o.z + (-p.x * s + p.z * c) * scale };
}

/** True when an actor of `radius` standing at `p` is inside `bounds` and clear of every collider. */
export type StandCheck = (p: Vec2) => boolean;

function placeSpec(spec: SeatSpec, idPrefix: string, origin: Vec2, yaw: number, scale: number, free: StandCheck): Seat[] {
  return spec.spots.map((spot, k) => {
    const pos = toRoom(origin, yaw, scale, spot);
    const candidates = spot.stand.map((p) => toRoom(origin, yaw, scale, p));
    const stand = candidates.find(free) ?? candidates[0]!;
    return { id: `${idPrefix}:${k}`, kind: spot.kind, x: pos.x, z: pos.z, facing: wrapAngle(yaw + spot.facing), y: spot.y, stand, place: spec.place };
  });
}

function insideBounds(p: Vec2, b: Aabb, radius: number): boolean {
  return p.x >= b.minX + radius && p.x <= b.maxX - radius && p.z >= b.minZ + radius && p.z <= b.maxZ - radius;
}

const DEFAULT_RADIUS = WORLD_MANIFEST.movement.actorRadiusMetres;

/**
 * The seats of a home (home_room): one set per piece of furniture placed on the template's anchors. Ids are
 * `<anchorId>:<itemId>:<n>`; stand-up spots are clear of the walls and of every placed item.
 */
export function homeSeats(template: InteriorTemplate, placements: readonly PlacementRef[], radius = DEFAULT_RADIUS): Seat[] {
  const colliders = [...interiorColliders(template), ...interiorDecorationColliders(template, placements)];
  const free: StandCheck = (p) => insideBounds(p, template.bounds, radius) && !pointInsideAny(p.x, p.z, radius, colliders);
  const out: Seat[] = [];
  for (const p of [...placements].sort((a, b) => a.anchorId.localeCompare(b.anchorId))) {
    const spec = ITEM_SEATS[p.itemId];
    const anchor = template.decorationAnchors?.find((a) => a.id === p.anchorId);
    if (!spec || !anchor || !getCatalogItem(p.itemId)?.anchorTypes.includes(anchor.type)) continue;
    out.push(...placeSpec(spec, `${p.anchorId}:${p.itemId}`, anchor.position, p.rotation, 1, free));
  }
  return out;
}

/**
 * The seats of the street: the square benches and the yard items with seats of the owned homes
 * (`<plotId>:<anchorId>:<itemId>:<n>`). A yard seat's stand-up spot stays on its own plot, off the house and clear of
 * the plot's yard items, hedges and mailbox; a bench's is in front of it.
 * Bench ids: `bench:<propIndex>:<n>` in Central (the v4 ids), `bench:<propIndex>:<districtId>:<n>` in another district
 * (world v6), `propIndex` counting the bench's own district's prop list, like its prop collider id (plots.ts
 * propColliders), so a bench has the same id in the whole town and in its district's slice, and ids are unique
 * town-wide.
 * World v6 (ADR-0042): handed the WHOLE manifest these are the seats of the whole town (its one street room;
 * townSeats, town.ts); a district slice gives that district's (v5: the whole manifest meant Central). Yard items of
 * plots the manifest handed in does not hold are ignored.
 */
export function streetSeats(manifest: WorldManifest, yard: ReadonlyArray<PlacementRef & { propertyId: string }>): Seat[] {
  const street = manifest;
  const radius = street.movement.actorRadiusMetres;
  const out: Seat[] = [];
  const statics = buildColliderIndex(street.colliders.map((c) => c.aabb));
  const propIndex = new Map<string, number>();
  for (const prop of street.props) {
    const districtId = itemDistrictId(street, prop);
    const i = propIndex.get(districtId) ?? 0;
    propIndex.set(districtId, i + 1);
    if (prop.kind !== "bench") continue;
    const free: StandCheck = (p) => !pointInsideAny(p.x, p.z, radius, statics);
    const prefix = districtId === CENTRAL_DISTRICT_ID ? `bench:${i}` : `bench:${i}:${districtId}`;
    out.push(...placeSpec(BENCH_SEATS, prefix, prop.position, prop.rotation, prop.scale, free));
  }
  const seated = yard.filter((p) => ITEM_SEATS[p.itemId]);
  if (!seated.length) return out;
  const owned = new Set(yard.map((p) => p.propertyId));
  const decorByPlot = new Map<string, Aabb[]>();
  for (const piece of streetDecor(street, owned, fencedPlotIds(yard))) {
    if (!piece.solid || !piece.plotId || !owned.has(piece.plotId)) continue;
    const list = decorByPlot.get(piece.plotId) ?? [];
    list.push(piece.aabb);
    decorByPlot.set(piece.plotId, list);
  }
  const sorted = [...seated].sort((a, b) => a.propertyId.localeCompare(b.propertyId) || a.anchorId.localeCompare(b.anchorId));
  for (const p of sorted) {
    const plot = street.plots.find((x) => x.id === p.propertyId);
    const anchor = plot?.decorationAnchors.find((a) => a.id === p.anchorId);
    const spec = ITEM_SEATS[p.itemId]!;
    if (!plot || plot.type !== "RESIDENTIAL" || !anchor || !getCatalogItem(p.itemId)?.anchorTypes.includes(anchor.type)) continue;
    const blockers = [homeCollider(plot), ...yardDecorationColliders(street, yard.filter((y) => y.propertyId === plot.id)), ...(decorByPlot.get(plot.id) ?? [])];
    const free: StandCheck = (q) => insideBounds(q, plot.bounds, radius) && !pointInsideAny(q.x, q.z, radius, blockers);
    out.push(...placeSpec(spec, `${p.propertyId}:${p.anchorId}:${p.itemId}`, anchor.position, p.rotation, 1, free).map((seat) => ({ ...seat, plotId: plot.id })));
  }
  return out;
}

/**
 * ADR-0019: the seats `actorId` may sit or lie on, given the closed homes (plot id -> the owner's actor id): a closed
 * home's yard seats are its owner's; every other seat is anyone's. The server applies the same rule (StreetRoom).
 */
export function mayUseSeat(seat: Pick<Seat, "plotId">, actorId: string | null, closedHomeOwners: ReadonlyMap<string, string | null>): boolean {
  if (!seat.plotId || !closedHomeOwners.has(seat.plotId)) return true;
  const owner = closedHomeOwners.get(seat.plotId) ?? null;
  return owner !== null && owner === actorId;
}

/**
 * The seats of a store (store_room) furnished with `themeId` (unknown / null falls back to "general", like the
 * colliders). Ids are `fx:<fixtureIndex>:<kind>:<n>`.
 */
export function storeSeats(themeId: StoreThemeId | string | null | undefined, radius = DEFAULT_RADIUS): Seat[] {
  const bounds = STORE_ROOM_BOUNDS;
  const colliders = storeInteriorColliders({ bounds }, themeId);
  const free: StandCheck = (p) => insideBounds(p, bounds, radius) && !pointInsideAny(p.x, p.z, radius, colliders);
  const out: Seat[] = [];
  storeTheme(themeId).fixtures.forEach((f, i) => {
    const spec = FIXTURE_SEATS[f.kind];
    if (spec) out.push(...placeSpec(spec, `fx:${i}:${f.kind}`, f.position, f.rotation, 1, free));
  });
  return out;
}

// ---------------------------------------------------------------------------------------------
// Queries shared by the server and the client
// ---------------------------------------------------------------------------------------------

/** Seats by id. */
export function seatIndex(seats: readonly Seat[]): Map<string, Seat> {
  return new Map(seats.map((s) => [s.id, s]));
}

/** Two seat lists describe the same seat for `id` (position, height, facing and kind): nobody needs to stand up. */
export function sameSeat(a: Seat | undefined, b: Seat | undefined): boolean {
  if (!a || !b) return false;
  return a.kind === b.kind && Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.z - b.z) < 1e-6 && Math.abs(a.y - b.y) < 1e-6 && Math.abs(wrapAngle(a.facing - b.facing)) < 1e-6;
}

/**
 * Distance from a standing actor to a seat: to the hips for a sit spot; for a lie spot, to the nearest point of the body
 * line (head to feet), so a bed can be reached from its side or its foot.
 */
export function seatDistance(seat: Seat, p: Vec2): number {
  if (seat.kind === "sit") return distance(p, seat);
  const f = forward(seat.facing);
  const ax = seat.x - f.x * LIE_HEAD_TO_HIPS;
  const az = seat.z - f.z * LIE_HEAD_TO_HIPS;
  const len = LIE_HEAD_TO_HIPS + LIE_HIPS_TO_FEET;
  const t = Math.max(0, Math.min(len, (p.x - ax) * f.x + (p.z - az) * f.z));
  return Math.hypot(p.x - (ax + f.x * t), p.z - (az + f.z * t));
}

/** The nearest seat within `reach` that nobody occupies (`taken` = seat ids in use), or null. */
export function nearestFreeSeat(seats: readonly Seat[], p: Vec2, taken: ReadonlySet<string>, reach = SEAT_REACH_METRES): Seat | null {
  let best: Seat | null = null;
  let bestD = Infinity;
  for (const s of seats) {
    if (taken.has(s.id)) continue;
    const d = seatDistance(s, p);
    if (d <= reach && d < bestD) {
      best = s;
      bestD = d;
    }
  }
  return best;
}

/**
 * Where a seated actor stands up: the seat's stand-up spot when it is inside `bounds`, clear of `colliders` and of
 * everyone else (`occupied`), otherwise the nearest such spot around it (rings of 0.25 m, 16 directions, up to 3 m),
 * otherwise the stand-up spot itself.
 */
export function standUpSpot(seat: Seat, colliders: ColliderSet, bounds: Aabb, occupied: readonly Vec2[], radius: number): Vec2 {
  const free = (p: Vec2) => insideBounds(p, bounds, radius) && !pointInsideAny(p.x, p.z, radius, colliders) && !occupied.some((o) => distance(o, p) < radius * 2);
  if (free(seat.stand)) return { x: seat.stand.x, z: seat.stand.z };
  for (let ring = 1; ring <= 12; ring++) {
    const d = ring * 0.25;
    for (let k = 0; k < 16; k++) {
      const a = (k / 16) * Math.PI * 2;
      const c = { x: seat.stand.x + Math.sin(a) * d, z: seat.stand.z + Math.cos(a) * d };
      if (free(c)) return c;
    }
  }
  return { x: seat.stand.x, z: seat.stand.z };
}
